/**
 * Origin-locks a post-auth `returnTo` value so it can only ever resolve back to the
 * application's own origin, closing the open-redirect token-leak vector.
 *
 * Strategy is **resolve-then-compare-origin**: parse `returnTo` relative to `base`
 * with the WHATWG URL API, accept only when the resolved origin equals the base
 * origin, and return the normalized same-origin relative target
 * (`pathname + search + hash`). Anything that resolves off-origin — an absolute URL,
 * a protocol-relative `//host`, a backslash `/\host`, or a value abusing
 * tab/newline/CR normalization (`/\t/host`) — returns `null`, as does empty input
 * or any value that throws during parsing.
 *
 * The returned relative target is itself re-parsed and origin-checked before being
 * returned (QL-440): dot-segment normalization can turn an input like `/.//evil.example`
 * into a pathname starting with `//`, which passes the first origin check (the resolved
 * URL is on-origin) yet re-parses as a protocol-relative `//evil.example` when callers
 * feed the returned path to `new URL(path, base)` or `window.location`. The artifact we
 * hand back is therefore held to the same standard as the input.
 *
 * This is deliberately *not* a raw-string prefix check (`startsWith("/")`): the URL
 * parser strips ASCII tab/newline/CR before parsing, so `"/\t/evil"` would pass a
 * prefix check yet resolve off-origin. The resolved origin — not the raw string —
 * is the validation boundary.
 *
 * @param returnTo The untrusted, caller-supplied redirect target.
 * @param base The trusted application origin to resolve and compare against.
 * @returns A normalized same-origin relative path, or `null` if rejected.
 */
export function sanitizeReturnTo(returnTo: string | null | undefined, base: string): string | null {
  if (typeof returnTo !== "string" || returnTo === "") {
    return null;
  }

  try {
    const baseOrigin = new URL(base).origin;
    const resolved = new URL(returnTo, base);
    if (resolved.origin !== baseOrigin) {
      return null;
    }
    const normalized = `${resolved.pathname}${resolved.search}${resolved.hash}`;
    if (new URL(normalized, base).origin !== baseOrigin) {
      return null;
    }
    // A `//`-prefixed pathname re-parses on-origin only against this same base;
    // a future consumer resolving it against a different base would resurrect
    // the protocol-relative bug. No legitimate app route starts with `//`.
    if (resolved.pathname.startsWith("//")) {
      return null;
    }
    return normalized;
  } catch {
    return null;
  }
}
